import { Extension, Mark, mergeAttributes, Node } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

/**
 * Keep hand-authored HTML intact through a Tiptap round-trip.
 *
 * Tiptap parses into a schema and re-serialises from it, so anything the schema does not model is dropped
 * — silently, and destructively: the editor emits its own `getHTML()` on change, and that string is what
 * gets sent. A designed email template (nested `<div>`s carrying inline styles for borders, backgrounds and
 * spacing) therefore arrives as a stack of bare paragraphs, both on screen and in the recipient's inbox.
 *
 * These extensions are OPT-IN (`preserveHtml` on TiptapEditor) rather than part of the default schema. The
 * editor has eight consumers; widening what every one of them accepts to fix one template would change how
 * unrelated content round-trips, for no benefit to those callers.
 *
 * Scope is deliberately narrow — the structural containers (divs, and the tables every designed email is built
 * from), the images, rules, headings and quotes ordinary templates carry (a signature with its logo, a dividing
 * line), the inline styles sent to email clients, and an editor-only semantic role used by the app preview. Email HTML cannot use stylesheets (clients strip
 * `<style>`), so `style` remains the outbound formatting layer. The preview role is a ProseMirror decoration:
 * it exists in the editor DOM for dark-mode CSS but never appears in `getHTML()` or the sent email.
 */

/** The `style` attribute, parsed and re-rendered verbatim. Shared by the node and the global pass. */
const styleAttribute = {
    style: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) => element.getAttribute('style'),
        renderHTML: (attributes: Record<string, unknown>) => (attributes.style ? { style: attributes.style as string } : {}),
    },
}

export type HtmlPreviewRoleResolver = (element: HTMLElement) => string | null

interface PreservedHtmlOptions {
    previewRoleResolver?: HtmlPreviewRoleResolver
}

/** Optional semantic role supplied by a caller. The generic editor stores it but never owns feature-specific
 * matching rules or includes the role in serialized HTML. */
const emailThemeAttribute = (previewRoleResolver?: HtmlPreviewRoleResolver) => ({
    emailTheme: {
        default: null,
        parseHTML: (element: HTMLElement) => previewRoleResolver?.(element) ?? null,
        renderHTML: () => ({}),
    },
})

const emailThemePreviewPluginKey = new PluginKey('emailThemePreview')

/** Plain HTML attributes an email layout relies on (widths, alignment, spacing), parsed and re-rendered verbatim. */
const verbatimAttributes = (names: readonly string[]) =>
    Object.fromEntries(
        names.map((name) => [
            name,
            {
                default: null as string | null,
                parseHTML: (element: HTMLElement) => element.getAttribute(name),
                renderHTML: (attributes: Record<string, unknown>) => (attributes[name] == null ? {} : { [name]: attributes[name] as string }),
            },
        ])
    )

// What a designed email's badge span carries beyond the properties the toolbar's marks own (colour, size,
// highlight): its box. An allowlist, not "everything else": rich text pasted from Google Docs or Word puts
// font-family, `font-weight: 400`, white-space and more on every span — that must not reach the broker, and a
// kept `font-weight: 400` would make the Bold button do nothing.
const BADGE_SPAN_PROPERTY = /^(display|line-height|letter-spacing|text-transform|padding.*|margin.*|border.*)$/
const isBold = (weight: string) => /^(bold|bolder|[6-9]00)$/.test(weight.trim())
const keepSpanProperty = (property: string, value: string) => BADGE_SPAN_PROPERTY.test(property) || (property === 'font-weight' && isBold(value))
const spanExtraStyleAttribute = {
    spanStyle: {
        default: null as string | null,
        parseHTML: (element: HTMLElement) => {
            const extra = Array.from(element.style)
                .map((property) => [property, element.style.getPropertyValue(property)] as const)
                .filter(([property, value]) => keepSpanProperty(property, value))
                .map(([property, value]) => `${property}: ${value}`)
            return extra.length > 0 ? extra.join('; ') : null
        },
        renderHTML: (attributes: Record<string, unknown>) => (attributes.spanStyle ? { style: attributes.spanStyle as string } : {}),
    },
}

/**
 * Text that sits directly inside a cell or div, next to (or instead of) block content.
 *
 * ProseMirror wraps such text in the container's first allowed textblock. Were that a paragraph, every
 * `<td>$1,100</td>` would come back as `<td><p>$1,100</p></td>` — and email clients give `<p>` margins, so the
 * layout would shift. This run renders as a plain `<span>`, which no client styles. It has no parse rule:
 * it is only ever created by that wrapping, and a bare `<span>` read back wraps into a run again.
 */
const HtmlInlineRun = Node.create({
    name: 'htmlInlineRun',
    content: 'inline*',
    // Ahead of the core keymap so Enter is caught here first.
    priority: 1000,

    parseHTML() {
        return []
    },

    renderHTML() {
        return ['span', 0]
    },

    addKeyboardShortcuts() {
        // Splitting a run would leave two spans side by side — one line in the email. A line break is what Enter means here.
        return {
            Enter: () => this.editor.state.selection.$from.parent.type.name === this.name && this.editor.commands.setHardBreak(),
        }
    },
})

/**
 * StarterKit's Bold reads a bold `font-weight` on ANY element as bold text. Here that weight is already kept in
 * the element's own style, so Bold would add a `<strong>` on top — which browsers draw one step bolder (800
 * becomes 900). This rule claims `font-weight` before Bold does and adds no mark; `<b>` and `<strong>` still parse.
 */
const KeepFontWeightInStyle = Mark.create({
    name: 'keepFontWeightInStyle',

    parseHTML() {
        return [{ style: 'font-weight', priority: 60, clearMark: () => false }]
    },

    renderHTML() {
        return ['span', 0]
    },
})

/** Content of a container that may hold bare text as well as blocks. The run comes first, so it is what wraps bare text. */
const MIXED_CONTENT = 'htmlInlineRun | block'

/**
 * Tables, rows and cells kept exactly as written: a designed email is laid out with tables, because Outlook
 * ignores CSS layout. Not prosemirror-tables — that normalises tables for spreadsheet-style editing (adding
 * cells to make rows even, column widths, wrapping text in paragraphs), which would change the layout. Here the
 * operator edits the text inside the cells; the grid itself only changes by pasting a new design.
 */
const HtmlTable = Node.create({
    name: 'htmlTable',
    group: 'block',
    content: 'htmlTableRow+',
    isolating: true,

    addAttributes() {
        return { ...styleAttribute, ...verbatimAttributes(['width', 'height', 'align', 'role', 'cellpadding', 'cellspacing', 'border', 'bgcolor']) }
    },

    parseHTML() {
        return [{ tag: 'table' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['table', mergeAttributes(HTMLAttributes), ['tbody', 0]]
    },
})

const HtmlTableRow = Node.create({
    name: 'htmlTableRow',
    content: 'htmlTableCell+',

    addAttributes() {
        return { ...styleAttribute, ...verbatimAttributes(['height', 'align', 'valign', 'bgcolor']) }
    },

    parseHTML() {
        return [{ tag: 'tr' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['tr', mergeAttributes(HTMLAttributes), 0]
    },
})

const HtmlTableCell = Node.create({
    name: 'htmlTableCell',
    content: `(${MIXED_CONTENT})+`,
    isolating: true,
    defining: true,

    addAttributes() {
        return {
            ...styleAttribute,
            ...verbatimAttributes(['width', 'height', 'align', 'valign', 'bgcolor', 'colspan', 'rowspan']),
            header: { default: false, parseHTML: (element: HTMLElement) => element.tagName === 'TH', renderHTML: () => ({}) },
        }
    },

    parseHTML() {
        return [{ tag: 'td' }, { tag: 'th' }]
    },

    renderHTML({ node, HTMLAttributes }) {
        return [node.attrs.header ? 'th' : 'td', mergeAttributes(HTMLAttributes), 0]
    },
})

/**
 * A `<div>` that survives the round-trip, with its children.
 *
 * Block content is what lets the card's nested structure through — a div holding paragraphs holding
 * more divs; bare text inside it becomes an HtmlInlineRun rather than a paragraph. `defining: true` keeps the wrapper when its content is replaced, so editing the text inside a
 * styled panel does not dissolve the panel.
 */
const HtmlBlock = Node.create<PreservedHtmlOptions>({
    name: 'htmlBlock',
    group: 'block',
    content: `(${MIXED_CONTENT})+`,
    defining: true,

    addOptions() {
        return { previewRoleResolver: undefined }
    },

    addAttributes() {
        return { ...styleAttribute, ...emailThemeAttribute(this.options.previewRoleResolver) }
    },

    parseHTML() {
        return [{ tag: 'div' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes), 0]
    },
})

/**
 * Carry inline styles and preview roles on the nodes that already exist in the schema.
 *
 * Without this the divs survive but everything inside them renders unstyled — the card's colours, sizes and
 * borders live on its paragraphs and links, not only on its containers.
 */
const PreserveInlineStyles = Extension.create<PreservedHtmlOptions>({
    name: 'preserveInlineStyles',

    addOptions() {
        return { previewRoleResolver: undefined }
    },

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'htmlBlock', 'bulletList', 'orderedList', 'listItem', 'link', 'bold', 'italic', 'underline', 'strike'],
                attributes: styleAttribute,
            },
            {
                types: ['paragraph'],
                attributes: emailThemeAttribute(this.options.previewRoleResolver),
            },
            {
                types: ['textStyle'],
                attributes: spanExtraStyleAttribute,
            },
        ]
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: emailThemePreviewPluginKey,
                props: {
                    decorations(state) {
                        const decorations: Decoration[] = []
                        state.doc.descendants((node, pos) => {
                            const role = node.attrs.emailTheme
                            if (typeof role === 'string' && role) {
                                decorations.push(Decoration.node(pos, pos + node.nodeSize, { 'data-email-theme': role }))
                            }
                        })
                        return DecorationSet.create(state.doc, decorations)
                    },
                },
            }),
        ]
    },
})

/**
 * The remaining tags saved templates carry — a signature's logo `<img>`, an `<hr>` dividing line, a heading, a
 * quote — kept as written. Without them the editor could not hold such a template at all, and it was shown
 * read-only. Like the tables, these are not StarterKit's editing nodes: those come with typing shortcuts ("# "
 * makes a heading, "---" a rule) that would surprise someone writing an email, and drop the attributes.
 */
const HtmlImage = Node.create({
    name: 'htmlImage',
    group: 'inline',
    inline: true,
    atom: true,
    draggable: true,

    addAttributes() {
        return { ...styleAttribute, ...verbatimAttributes(['src', 'alt', 'title', 'width', 'height']) }
    },

    parseHTML() {
        return [{ tag: 'img[src]' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes)]
    },
})

const HtmlRule = Node.create({
    name: 'htmlRule',
    group: 'block',
    atom: true,

    addAttributes() {
        return { ...styleAttribute, ...verbatimAttributes(['width', 'size', 'color', 'align', 'noshade']) }
    },

    parseHTML() {
        return [{ tag: 'hr' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['hr', mergeAttributes(HTMLAttributes)]
    },
})

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const

const HtmlHeading = Node.create({
    name: 'htmlHeading',
    group: 'block',
    content: 'inline*',
    defining: true,

    addAttributes() {
        return { ...styleAttribute, ...verbatimAttributes(['align']), level: { default: 2, renderHTML: () => ({}) } }
    },

    parseHTML() {
        return HEADING_LEVELS.map((level) => ({ tag: `h${level}`, attrs: { level } }))
    },

    renderHTML({ node, HTMLAttributes }) {
        return [`h${node.attrs.level}`, mergeAttributes(HTMLAttributes), 0]
    },
})

const HtmlQuote = Node.create({
    name: 'htmlQuote',
    group: 'block',
    content: `(${MIXED_CONTENT})+`,
    defining: true,

    addAttributes() {
        return styleAttribute
    },

    parseHTML() {
        return [{ tag: 'blockquote' }]
    },

    renderHTML({ HTMLAttributes }) {
        return ['blockquote', mergeAttributes(HTMLAttributes), 0]
    },
})

/** Everything `preserveHtml` adds to the editor. One list, so no caller can include the containers without the run they need. */
export const preservedHtmlExtensions = (previewRoleResolver?: HtmlPreviewRoleResolver) => [
    HtmlInlineRun,
    HtmlBlock.configure({ previewRoleResolver }),
    HtmlTable,
    HtmlTableRow,
    HtmlTableCell,
    HtmlImage,
    HtmlRule,
    HtmlHeading,
    HtmlQuote,
    KeepFontWeightInStyle,
    PreserveInlineStyles.configure({ previewRoleResolver }),
]
